import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { toast } from 'sonner';

const BANNER = String.raw`
   █████╗ ████████╗██████╗ ███████╗
  ██╔══██╗╚══██╔══╝██╔══██╗██╔════╝
  ███████║   ██║   ██║  ██║█████╗
  ██╔══██║   ██║   ██║  ██║██╔══╝
  ██║  ██║   ██║   ██████╔╝███████╗
  ╚═╝  ╚═╝   ╚═╝   ╚═════╝ ╚══════╝
`;

const BOOT = [
  '[atde] ► boot sequence initiated',
  '[atde] ► firewall..........ONLINE',
  '[atde] ► quant engine......ONLINE',
  '[atde] ► discovery grid....ONLINE',
  '[atde] ► ai reasoner.......ONLINE',
  '[atde] ► awaiting operator credentials...',
];

export default function Login() {
  const { login, user } = useAuth();
  const nav = useNavigate();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [shown, setShown] = useState(0);

  useEffect(() => {
    if (shown >= BOOT.length) return;
    const t = setTimeout(() => setShown((s) => s + 1), 180);
    return () => clearTimeout(t);
  }, [shown]);

  useEffect(() => { if (user) nav('/app/dashboard'); }, [user, nav]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setErr('');
    try {
      await login(username, password);
      toast.success('ACCESS GRANTED', { description: 'Welcome back, operator.' });
      nav('/app/dashboard');
    } catch (e2) {
      const msg = e2?.response?.data?.detail || 'Failed Access';
      setErr(msg);
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4 scanlines">
      <div className="panel brackets w-full max-w-lg p-6" data-testid="login-panel">
        <span className="bk-bl" /><span className="bk-br" />
        <pre className="text-cyan-sys text-[10px] md:text-[12px] leading-tight whitespace-pre mb-2 glow-cyan">
{BANNER}
        </pre>
        <div className="font-display tracking-[0.4em] text-[11px] text-[var(--txt-secondary)] mb-5 text-center uppercase">
          AI TRADING DECISION ENGINE // SECURE TERMINAL
        </div>
        <div className="font-mono text-[11px] mb-4 text-[var(--txt-secondary)] min-h-[90px]">
          {BOOT.slice(0, shown).map((line, i) => (
            <div key={i} className={line.includes('ONLINE') ? 'text-buy' : 'text-cyan-sys'}>{line}</div>
          ))}
        </div>
        <form onSubmit={submit} className="space-y-3">
          <label className="block">
            <span className="block text-[10px] tracking-[0.3em] text-cyan-sys font-display uppercase mb-1">operator:_</span>
            <input
              data-testid="login-username"
              autoFocus
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              className="w-full bg-black border border-[var(--border-muted)] focus:border-cyan-sys outline-none px-3 py-2 font-mono text-[13px] text-cyan-sys"
              placeholder=">"
              autoComplete="username"
            />
          </label>
          <label className="block">
            <span className="block text-[10px] tracking-[0.3em] text-cyan-sys font-display uppercase mb-1">passkey:_</span>
            <input
              data-testid="login-password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="w-full bg-black border border-[var(--border-muted)] focus:border-cyan-sys outline-none px-3 py-2 font-mono text-[13px] text-cyan-sys tracking-[0.4em]"
              placeholder="******"
              autoComplete="current-password"
            />
          </label>
          {err && <div data-testid="login-error" className="text-sell text-[11px] font-mono">// {err}</div>}
          <button
            data-testid="login-submit"
            disabled={busy}
            className="w-full font-display tracking-[0.35em] uppercase text-sm py-2.5 border border-cyan-sys text-cyan-sys hover:bg-cyan-sys hover:text-black disabled:opacity-50 transition-colors"
          >
            {busy ? '// authenticating...' : '→ authenticate'}
          </button>
        </form>
        <div className="mt-4 text-center text-[10px] text-[var(--txt-muted)] tracking-widest uppercase">
          all access attempts are logged
        </div>
      </div>
    </div>
  );
}
